/* eslint-disable import/order */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as R from 'ramda';
import { bool, func, number } from 'prop-types';
import { Button, useMediaQuery, useTheme } from '@mui/material';

// Context
import { DrawerContext } from 'common/drawer/DrawerContext';

// Components
import { PlaceholderPage } from 'common';
import CardsPanel from './cards-panel/CardsPanel.connector';
import CreateWizard from './create-new-steps/CreateWizard';
import MobileDrawerConnector from '../mobile-drawer/MobileDrawer.connector';
import Sidebar from './sidebar/Sidebar.connector';

// Utils
import { DRAWER_TYPES } from 'utils/constants';
import { capitalize } from 'utils/helpers/format';
import { setDialogDetails } from 'feature/loading-management/supplier-authorizations/store/supplierAuthorizationSlice';
import { CARRIER_AUTH_ROUTES } from '../utils/const';
import { resetCarrierAuthWizardData, setForceClose } from './store/createNewCarrierAuthSlice';
import allCarrierAuthSteps from './create-new-steps/Steps';
import EditDrawer from './edit/EditDrawer.connector';
import CarrierAuthGrid from './carrier-authorizations-grid/CarrierAuthGrid';
import { EvaluateFlag } from 'common/FeatureFlags/Flag';
import FilterableDataGrid from 'common/templates/FilterableDataGrid';
import { useUserRolePermissions } from 'hooks';
import CarrierAuthorizationsFilters from './carrier-authorizations-grid/CarrierAuthorizationsFilters';
import useCarrierAuthFilters from './carrier-authorizations-grid/useCarrierAuthFilters';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { openConfirmationDialog } from 'store/Confirmationdialogslice';

const CarrierAuthorizations = ({
  carrierAuthorizationsCardsLength,
  filtersOpen,
  resetCarrierAuthorizations,
  resetFilters,
  setAuthorizationBy,
  setFiltersOpen,
  setMobileDrawerOpen,
}) => {
  const theme = useTheme();
  const lgUp = useMediaQuery(theme.breakpoints.up('lg'), { noSsr: true });
  const { viewBy, viewById } = useParams();

  const { setDrawerComponentProps } = useContext(DrawerContext);
  const { isProductLoadingManage } = useUserRolePermissions();

  const [isEditing, setIsEditing] = useState(false);
  const [carrierAuthorizationId, setCarrierAuthorizationId] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const carrierAuthGridFlag = EvaluateFlag(process.env.REACT_APP_LD_CARRIER_AUTH_LIST_VIEW);

  // Use the custom hook to manage filter state
  const {
    affiliateDisplayOptions,
    appliedFilters,
    carrierOptions,
    carriersLoading,
    draftFilters,
    handleApply,
    handleReset,
    hasChanges,
    isOnDefaultState,
    locationOptions,
    locationsLoading,
    selectedAffiliate,
    setAffiliateFilter,
    setCarrierFilter,
    setInsuranceExpiration,
    setLocationFilter,
    setSearch,
    showAffiliateFilter,
  } = useCarrierAuthFilters();

  useEffect(() => {
    setAuthorizationBy(viewBy);
    return () => {
      // this resets everything before fetchSelectors fires when changing views
      // do not need to reset this data or any other carrier auth data in selectors fetch
      resetCarrierAuthorizations();
      resetFilters();
      setMobileDrawerOpen(false);
      setAuthorizationBy('carrier');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewBy]);

  if (R.not(R.includes(viewBy, CARRIER_AUTH_ROUTES)))
    return <PlaceholderPage title="Carrier Authorizations" />;

  const openCreateWizard = () => {
    setDrawerComponentProps({
      Component: props => (
        <CreateWizard steps={allCarrierAuthSteps} title="New Carrier Authorizations" {...props} />
      ),
      drawerPaperSx: { overflowY: 'hidden', width: { lg: 760, xs: 1 } },
      forceCloseSelector: state => state.loadingManagement.createNewCarrierAuth.forceClose,
      holdOpenSelector: state => {
        const { hasUnsavedChanges, submittingBatch } = state.loadingManagement.createNewCarrierAuth;
        return hasUnsavedChanges || submittingBatch;
      },
      resetWizard: resetCarrierAuthWizardData,
      setCurrentDialogOpen: () => (dialogDispatch, getState) => {
        const { hasUnsavedChanges, submittingBatch } =
          getState().loadingManagement.createNewCarrierAuth;

        if (hasUnsavedChanges && !submittingBatch) {
          dialogDispatch(
            openConfirmationDialog({
              cancelAction: () => {},
              confirmAction: () => dialogDispatch(setForceClose(true)),
              header: 'Unsaved Changes',
              leftButtonActionType: 'confirm',
              leftButtonText: 'Continue',
              message:
                'Are you sure you want to leave this page? You have unsaved Carrier Authorizations.',
              params: {},
              rightButtonActionType: 'cancel',
              rightButtonText: 'Keep Editing',
            })
          );
          return;
        }

        dialogDispatch(
          setDialogDetails({
            header: 'Are you sure you want to close the wizard?',
            message:
              'By closing the wizard you will no longer be able to view the progress of each activation however, all requests will continue until complete. Please confirm you want to close the wizard.',
          })
        );
      },
      type: DRAWER_TYPES.createNewCarrierAuth,
    });
  };

  const handleNewCarrierAuth = () => {
    openCreateWizard();
  };

  const handleEdit = useCallback(id => {
    setCarrierAuthorizationId(id);
    setIsEditing(true);
  }, []);

  const resetCarrierAuthorizationId = () => {
    setCarrierAuthorizationId(null);
  };

  return (
    <>
      {carrierAuthGridFlag ? (
        <>
          <FilterableDataGrid
            filterContent={
              <CarrierAuthorizationsFilters
                affiliateOptions={affiliateDisplayOptions}
                carrierOptions={carrierOptions}
                carriersLoading={carriersLoading}
                disabled={isExporting}
                insuranceExpiration={draftFilters.insuranceExpiration}
                locationOptions={locationOptions}
                locationsLoading={locationsLoading}
                onAffiliateChange={setAffiliateFilter}
                onCarrierChange={setCarrierFilter}
                onInsuranceExpirationChange={setInsuranceExpiration}
                onLocationChange={setLocationFilter}
                selectedAffiliate={selectedAffiliate}
                selectedCarriers={draftFilters.carriers}
                selectedLocations={draftFilters.locations}
                showAffiliateFilter={showAffiliateFilter}
              />
            }
            handleReset={handleReset}
            hasChanges={hasChanges}
            headerTitle="Carrier Authorizations"
            isExporting={isExporting}
            isOnDefaultState={isOnDefaultState}
            onApply={handleApply}
            onSearchChange={setSearch}
            primaryFilterActionButtonLabel="Apply Filters"
            searchValue={draftFilters.search}
            secondaryFilterActionButtonLabel="Reset Filters"
            titleMenu={
              <>
                {isProductLoadingManage && (
                  <Button
                    onClick={handleNewCarrierAuth}
                    startIcon={<AddRoundedIcon />}
                    variant="vertical-with-icon-contained"
                  >
                    New Authorization
                  </Button>
                )}
              </>
            }
          >
            <CarrierAuthGrid
              appliedFilters={appliedFilters}
              onEdit={handleEdit}
              onExportingChange={setIsExporting}
            />
          </FilterableDataGrid>
        </>
      ) : (
        <>
          <Sidebar />
          {lgUp ? (
            <CardsPanel onEdit={handleEdit} viewBy={viewBy} viewById={viewById} />
          ) : (
            <MobileDrawerConnector
              activationClick={() => handleNewCarrierAuth()}
              cardsLength={carrierAuthorizationsCardsLength}
              filtersClick={() => setFiltersOpen(!filtersOpen)}
              panel={<CardsPanel onEdit={handleEdit} viewBy={viewBy} viewById={viewById} />}
              type={capitalize(viewBy)}
            />
          )}
        </>
      )}
      <EditDrawer
        carrierAuthorizationId={carrierAuthorizationId}
        isEditing={isEditing}
        resetCarrierAuthorizationId={resetCarrierAuthorizationId}
        setIsEditing={setIsEditing}
      />
    </>
  );
};

CarrierAuthorizations.propTypes = {
  carrierAuthorizationsCardsLength: number.isRequired,
  filtersOpen: bool.isRequired,
  resetCarrierAuthorizations: func.isRequired,
  resetFilters: func.isRequired,
  setAuthorizationBy: func.isRequired,
  setFiltersOpen: func.isRequired,
  setMobileDrawerOpen: func.isRequired,
};

export default CarrierAuthorizations;
